// Phase 5Z.9 — Fahrer Profile (read-only) + Phase 5Z.12 Change-Request workflow
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Info, Pencil, X, Check, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fahrerApi } from "./lib/api";

const EDITABLE_FIELDS = [
  { key: "phone", labelKey: "profile.phone", type: "tel" },
  { key: "private_email", labelKey: "profile.private_email", type: "email" },
  { key: "address", labelKey: "profile.address", type: "text" },
  { key: "address_extra", labelKey: "profile.address_extra", type: "text" },
  { key: "zip_code", labelKey: "profile.zip_code", type: "text" },
  { key: "city", labelKey: "profile.city", type: "text" },
  { key: "emergency_contact_name", labelKey: "profile.emergency_contact_name", type: "text" },
  { key: "emergency_contact_phone", labelKey: "profile.emergency_contact_phone", type: "tel" },
  { key: "emergency_contact_relationship", labelKey: "profile.emergency_contact_relationship", type: "text" },
];

export default function FahrerProfile() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        fahrerApi.get("/fahrer/profile"),
        fahrerApi.get("/fahrer/profile-change-requests"),
      ]);
      setProfile(p.data);
      setRequests(r.data || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-zinc-500 text-sm py-8 text-center">{t("app.loading")}</div>;
  if (!profile) return null;

  const pendingRequest = requests.find((r) => r.status === "pending");

  if (editing) {
    return (
      <ChangeRequestForm
        profile={profile}
        onCancel={() => setEditing(false)}
        onSubmitted={() => { setEditing(false); load(); }}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="fahrer-profile-page">
      <div>
        <h1 className="text-xl font-bold text-zinc-50">{t("profile.title")}</h1>
        <p className="text-xs text-zinc-500 mt-0.5">{t("profile.subtitle")}</p>
      </div>

      <Section title={t("profile.personal_data")}>
        <Row label={t("profile.first_name")} value={profile.first_name} />
        <Row label={t("profile.last_name")} value={profile.last_name} />
        <Row label={t("profile.email")} value={profile.email} />
        <Row label={t("profile.phone")} value={profile.phone} />
        <Row label={t("profile.address")} value={profile.address} />
        <Row label={t("profile.birth_date")} value={profile.birth_date} />
        <Row label={t("profile.nationality")} value={profile.nationality} />
        <Row label={t("profile.svnr")} value={profile.svnr} />
      </Section>

      <Section title={t("profile.employment")}>
        <Row label={t("profile.department")} value={profile.department_name} />
        <Row label={t("profile.role")} value={profile.role} />
        <Row label={t("profile.hire_date")} value={profile.hire_date} />
        <Row label={t("profile.contract_type")} value={profile.contract_type} />
        <Row label={t("profile.status")} value={profile.employment_status} />
      </Section>

      {pendingRequest ? (
        <PendingBanner request={pendingRequest} t={t} />
      ) : (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex gap-2 items-start" data-testid="fahrer-profile-change-notice">
          <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-zinc-300 flex-1">
            {t("profile.request_change_hint")}
            <div className="mt-2">
              <button
                onClick={() => setEditing(true)}
                data-testid="fahrer-profile-request-change"
                className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded text-white inline-flex items-center gap-1.5 font-medium"
              >
                <Pencil className="w-3 h-3" />
                {t("profile.request_change")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {requests.length > 0 && (
        <Section title={t("profile.history_title")}>
          {requests.map((r) => (
            <RequestRow key={r.id} request={r} t={t} />
          ))}
        </Section>
      )}
    </div>
  );
}

function ChangeRequestForm({ profile, onCancel, onSubmitted }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(() => {
    const v = {};
    EDITABLE_FIELDS.forEach(({ key }) => { v[key] = profile[key] || ""; });
    return v;
  });
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    const changes = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      const newVal = (values[key] || "").trim();
      const oldVal = (profile[key] || "").trim();
      if (newVal !== oldVal) changes[key] = newVal;
    });
    if (Object.keys(changes).length === 0) {
      toast.error(t("profile.no_changes"));
      return;
    }
    setSubmitting(true);
    try {
      await fahrerApi.post("/fahrer/profile-change-request", { changes, reason });
      toast.success(t("profile.request_submitted"));
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="space-y-4" data-testid="fahrer-change-request-form">
      <div>
        <h1 className="text-xl font-bold text-zinc-50">{t("profile.request_change")}</h1>
        <p className="text-xs text-zinc-500 mt-0.5">{t("profile.request_change_subtitle")}</p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
        {EDITABLE_FIELDS.map(({ key, labelKey, type }) => (
          <div key={key}>
            <label className="block text-xs text-zinc-400 mb-1">{t(labelKey)}</label>
            <input
              type={type}
              value={values[key]}
              onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
              data-testid={`fahrer-change-${key}`}
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-amber-500 focus:outline-none"
            />
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs text-zinc-400 mb-1">{t("profile.reason_label")}</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="fahrer-change-reason"
          rows={2}
          placeholder={t("profile.reason_placeholder")}
          className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-amber-500 focus:outline-none"
        />
      </div>

      <div className="flex gap-2">
        <button onClick={onCancel} data-testid="fahrer-change-cancel" className="flex-1 py-3 bg-zinc-800 text-zinc-100 rounded-lg text-sm">
          {t("common.cancel")}
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="fahrer-change-submit"
          className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 text-white rounded-lg text-sm font-semibold"
        >
          {submitting ? "…" : t("profile.submit_request")}
        </button>
      </div>
    </div>
  );
}

function PendingBanner({ request, t }) {
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="fahrer-profile-pending-banner">
      <div className="flex items-center gap-2 text-amber-300 mb-2">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-bold">{t("profile.pending_title")}</span>
      </div>
      <p className="text-xs text-zinc-300">{t("profile.pending_hint")}</p>
      <div className="mt-2 space-y-0.5 text-xs">
        {Object.entries(request.changes || {}).map(([k, v]) => (
          <div key={k} className="text-zinc-400">
            <span className="text-zinc-500">{k}:</span> <span className="text-zinc-100 font-medium">{v || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RequestRow({ request, t }) {
  const Icon =
    request.status === "approved" ? CheckCircle2 :
    request.status === "rejected" ? AlertTriangle :
    Clock;
  const color =
    request.status === "approved" ? "text-emerald-400" :
    request.status === "rejected" ? "text-red-400" :
    "text-amber-400";
  return (
    <div className="border-b border-zinc-800/50 last:border-b-0 py-2" data-testid={`fahrer-request-row-${request.id}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span className="text-xs text-zinc-300">{t(`profile.status_${request.status}`)}</span>
        <span className="text-[10px] text-zinc-500 ml-auto font-mono">{(request.submitted_at || "").slice(0, 10)}</span>
      </div>
      <div className="text-xs text-zinc-400 ml-5 mt-1">
        {Object.keys(request.changes || {}).length} {t("profile.fields")}
        {request.decision_reason && (
          <span className="text-red-400 italic"> · {request.decision_reason}</span>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/80">
        <h3 className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{title}</h3>
      </div>
      <div className="p-4 space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-1.5 border-b border-zinc-800/50 last:border-b-0">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className="text-sm text-zinc-100 text-right">{value || "—"}</span>
    </div>
  );
}
